import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from './EvaluationsView.module.css';
import { Col, Row } from "react-bootstrap";
import Langs from "../../app/lang/langs";
import { TeamSelector } from "../../components/teams/TeamSelector";
import { config } from "../../app/utils/config";
import { checkPermission, showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import {
    getEvaluationsReport,
    setEvaluationAddModal,
    setOnEvaluateCollab,
    setOnEvaluateDate,
    setOnEvaluateTeamId,
    setAddResponse,
    setEditResponse,
    setSelectedTypeGeneric,
    setSelectedType,
    getEvaluationsTypes,
    setAllowedToEdit,
    generateAnnualPrimeReportByType
} from "../../components/evaluations/evaluationsSlice";
import ImageButton from "../../components/buttons/ImageButton";
import exportIcon from "../../assets/images/export.png";
import exportIconFocus from "../../assets/images/exportFocus.png";
import ExcelManager from "../../app/managers/excelManager";
import { getCollabs } from "../../components/collabs/collabsSlice";
import moment from "moment";
import EvaluationTypeSelector from "../../components/evaluations/EvaluationTypeSelector";
import { useNavigate, useParams } from "react-router-dom";
import AddEvaluationModal from "./modals/AddEvaluationModal";
import EvaluationsToolbar from "./components/EvaluationsToolbar";
import EvaluationsTable from "./components/EvaluationsTable";
import MobileEvaluationsList from "./mobile/MobileEvaluationsList";
import MobileEvaluationsTopMenu from "./mobile/MobileEvaluationsTopMenu";
import MobileAddEvaluationModal from "./mobile/MobileAddEvaluationModal";
import {
    getAllowedTeamIds,
    getTeamMonthData,
    convertReportsToArray,
    processReports,
    summarizeReports
} from "./utils/evaluationUtils";


/**
 * Évaluations — one view for every evaluation type.
 *
 * Previously three: a hardcoded Collab / DM / Référent SelectButton switching between an
 * inline render and two container files that differed from each other by a single type-id
 * constant. Types are data now, so a fourth one had nowhere to appear. The header picker
 * is driven by the type list and this component renders whichever is selected.
 *
 * The URL's third segment carries the evaluation type id (it used to be a 0/1/2 view
 * index), so a link points at a type rather than at a position in a fixed list.
 */
export default function EvaluationsView() {

    const dispatch = useDispatch();
    const urlParams = useParams();
    const navigate = useNavigate();

    const selectedTeams = useSelector((state) => state.teams.selectedTeamsIds);
    const reports = useSelector((state) => state.evaluations.report);
    const myTeam = useSelector((state) => state.teams.myTeam);
    const addEvaluationRes = useSelector((state) => state.evaluations.addResponse);
    const editEvaluationRes = useSelector((state) => state.evaluations.editResponse);
    const types = useSelector((state) => state.evaluations.genericTypes);
    const selectedType = useSelector((state) => state.evaluations.selectedTypeGeneric);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mounted = useRef(false);

    const [expandedTeamId, setExpandedTeamId] = useState(null);
    const [date, setDate] = useState(new Date());
    const [onViewTeam, setOnViewTeam] = useState(null);
    const [selectedTeamId, setSelectedTeamId] = useState();

    // ─── Derived values ───────────────────────────────────────────────────────

    const year = useMemo(() => date.getFullYear(), [date]);

    const allowedTeams = useMemo(
        () => (selectedTeams?.length ? getAllowedTeamIds(selectedTeams) : []),
        [selectedTeams]
    );

    const groupedReports = useMemo(
        () => convertReportsToArray(reports, year),
        [reports, year]
    );

    const processedReports = useMemo(
        () => processReports(groupedReports, year),
        [groupedReports, year]
    );

    const summarizedReport = useMemo(
        () => summarizeReports(processedReports),
        [processedReports]
    );

    const visibleTeams = useMemo(() =>
        allowedTeams.filter(id =>
            !myTeam ||
            checkPermission(config.scopes.evaluations, config.permissionTypes.view, myTeam.id) ||
            myTeam.id !== id
        ),
        [allowedTeams, myTeam]
    );

    const monthColumns = useMemo(() =>
        Array.from({ length: 12 }, (_, index) => ({
            key: index + 1,
            header: (
                <div>
                    <Langs str={new Date(2000, index).toLocaleString('default', { month: 'short' })} />
                    <br />
                    {getTeamMonthData(summarizedReport, onViewTeam, index + 1)}
                </div>
            ),
        })),
        [summarizedReport, onViewTeam]
    );

    const groupedData = useMemo(() => {
        const groups = {};
        groupedReports.forEach(row => {
            if (!groups[row.id_team]) {
                groups[row.id_team] = { team_name: row.team_name, id_team: row.id_team, rows: [] };
            }
            groups[row.id_team].rows.push(row);
        });
        return Object.values(groups);
    }, [groupedReports]);

    // ─── Effects ─────────────────────────────────────────────────────────────

    useEffect(() => {
        mounted.current = true;
    }, []);

    useEffect(() => {
        if (mounted.current && selectedTeams?.length) {
            dispatch(getCollabs({ selectedTeamsIds: selectedTeams }));
        }
    }, [selectedTeams]);

    useEffect(() => {
        if (selectedTeams?.length) {
            dispatch(getEvaluationsTypes({ idTeam: selectedTeams }));
        }
    }, [selectedTeams]);

    useEffect(() => {
        if (year && selectedType && allowedTeams.length) {
            dispatch(getEvaluationsReport({
                idTeams: allowedTeams,
                year,
                idType: selectedType.id,
            }));
        }
    }, [allowedTeams, year, selectedType]);

    useEffect(() => {
        if (selectedTeams.length === 1) {
            setOnViewTeam(selectedTeams[0]);
        } else if (selectedTeams.length > 1) {
            setOnViewTeam(null);
        }
    }, [selectedTeams]);

    // The URL's third segment is an evaluation type id. Only adopt it once the type list
    // has loaded and actually contains it — a type scoped to other teams must not be
    // selectable just because it is in someone's link.
    useEffect(() => {
        if (!urlParams.subView || !types?.length) return;

        const fromUrl = types.find(t => String(t.id) === String(urlParams.subView));
        if (fromUrl && String(fromUrl.id) !== String(selectedType?.id)) {
            dispatch(setSelectedTypeGeneric(fromUrl));
        }
    }, [urlParams.subView, types]);

    // ─── Response handlers ────────────────────────────────────────────────────

    const handleApiResponse = useCallback((res, clearAction) => {
        const lower = res.trim().toLowerCase();
        if (lower.includes("successfully")) {
            showSuccessToast("Success!");
        } else if (lower.includes("duplicate")) {
            showErrorToast(<Langs str="DUPICATE_WARNING" />);
        } else {
            showErrorToast(res);
        }
        dispatch(clearAction(null));
    }, [dispatch]);

    const refetchReport = useCallback(() => {
        if (year && selectedType && allowedTeams.length) {
            dispatch(getEvaluationsReport({
                idTeams: allowedTeams,
                year,
                idType: selectedType.id,
            }));
        }
    }, [dispatch, year, selectedType, allowedTeams]);

    useEffect(() => {
        if (addEvaluationRes != null) {
            handleApiResponse(addEvaluationRes, setAddResponse);
            if (addEvaluationRes.trim().toLowerCase().includes("successfully")) {
                refetchReport();
            }
        }
    }, [addEvaluationRes]);

    useEffect(() => {
        if (editEvaluationRes != null) {
            handleApiResponse(editEvaluationRes, setEditResponse);
            if (editEvaluationRes.trim().toLowerCase().includes("successfully")) {
                refetchReport();
            }
        }
    }, [editEvaluationRes]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    // Selecting a type navigates rather than setting state directly, so the URL stays the
    // single source of truth and the choice survives a reload or a shared link. The effect
    // above picks the change back up.
    const handleTypeChange = useCallback((idType) => {
        if (idType == null || !selectedTeams?.length) return;
        navigate(`/evaluations/${selectedTeams[0]}/${idType}`);
    }, [navigate, selectedTeams]);

    const toggleGroupExpansion = useCallback((teamId) => {
        setExpandedTeamId(prev => {
            const next = prev === teamId ? null : teamId;
            setOnViewTeam(next);
            return next;
        });
    }, []);

    const showModalEditAdd = useCallback((collab, date, entreeDate, sortieDate, id_team, dateStartMonth) => {
        const isAllowed =
            (entreeDate ? date.isAfter(entreeDate) : true) &&
            (sortieDate ? dateStartMonth.isBefore(moment(sortieDate)) : true);
        dispatch(setAllowedToEdit(isAllowed));
        dispatch(setOnEvaluateTeamId(id_team));
        dispatch(setOnEvaluateCollab(collab));
        dispatch(setOnEvaluateDate(date));
        dispatch(setEvaluationAddModal(true));
        dispatch(setSelectedType(selectedType));
    }, [dispatch, selectedType]);

    const exportExcel = useCallback(() => {
        const idType = parseInt(selectedType?.id);
        if (!idType || isNaN(idType)) return;

        // The grid shows every selected team, so the export does too. It used to send
        // only selectedTeams[0], which silently exported a subset.
        const idTeams = allowedTeams.length ? allowedTeams : selectedTeams;

        dispatch(generateAnnualPrimeReportByType({ idTeams, year, idType })).then(actionResponse => {
            if (!generateAnnualPrimeReportByType.fulfilled.match(actionResponse)) return;

            const responseData = actionResponse.payload?.responseData;
            // A type with no report configuration is a normal state — tell the user to
            // configure it rather than handing them an empty workbook.
            if (responseData?.configured === false) {
                showErrorToast(<Langs str="EVAL_CFG_NOT_CONFIGURED" />);
                return;
            }

            const exportData = responseData?.annual_report;
            if (!exportData || !exportData.length) {
                showErrorToast(<Langs str="EVAL_CFG_EMPTY_REPORT" />);
                return;
            }

            ExcelManager.getInstance().GenerateAndDownloadAnnualPrimeReportByType(exportData, {
                typeTitle: selectedType?.title,
            });
        });
    }, [dispatch, selectedTeams, allowedTeams, year, selectedType]);

    // ─── Render ───────────────────────────────────────────────────────────────

    const scrollHeight = isMobile === 1
        ? 'calc(100vh - 250px)'
        : isMobile === 3 ? 'calc(100vh - 165px)' : 'calc(100vh - 350px)';

    if (isMobile === 2) {
        return (
            <div className={styles.containerView} style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                <MobileEvaluationsTopMenu
                    types={types}
                    selectedTypeId={selectedType?.id}
                    onTypeChange={handleTypeChange}
                    date={date}
                    onDateChange={(val) => setDate(val)}
                    onExport={exportExcel}
                    showExport={selectedTeams.length === 1}
                />
                <MobileEvaluationsList
                    groupedData={groupedData}
                    visibleTeams={visibleTeams}
                    expandedTeamId={expandedTeamId}
                    onToggleGroup={toggleGroupExpansion}
                    year={year}
                    onClickCell={showModalEditAdd}
                />
                <MobileAddEvaluationModal selectedDate={date} selectedTeamId={selectedTeamId} />
            </div>
        );
    }

    // Desktop & tablet — original render
    return (
        <div className={styles.containerView}>
            <div className="topMenu" style={{ height: 'auto' }}>
                {isMobile !== 3 ? (
                    <Row>
                        <Col lg={5}>
                            <span className="headerTitle">
                                <Langs str="Evaluations" />
                            </span>
                            {(isMobile === 1 || isMobile === 3) && <TeamSelector includeMyTeam />}
                        </Col>
                        <Col lg={5} className="d-flex justify-content-start pe-4 align-items-center p-0 m-0">
                            <EvaluationTypeSelector
                                types={types}
                                value={selectedType?.id}
                                onChange={handleTypeChange}
                            />
                        </Col>
                        <Col lg={2} xs={12} className={(isMobile === 1 || isMobile === 3) ? "d-flex justify-content-end pe-4 align-items-center p-0 m-0" : "d-flex justify-content-between px-2 align-items-center p-0 m-0 mt-2"}>
                            {isMobile === 2 && <TeamSelector includeMyTeam />}
                            {/* The Référent view now exports too — the report comes from
                                its type configuration rather than a bespoke endpoint. */}
                            {selectedTeams.length === 1 &&
                                <ImageButton
                                    image={exportIcon}
                                    imageFocus={exportIconFocus}
                                    onClick={exportExcel}
                                    active={true}
                                    tooltip="Export"
                                    width={32}
                                />
                            }
                        </Col>
                    </Row>
                ) : (
                    <div></div>
                )}
            </div>
            <div className={`${styles.collabsCard} ${isMobile === 2 ? styles.mobileBody : ''}`}>
                <EvaluationsToolbar
                    date={date}
                    onDateChange={(val) => setDate(val)}
                    isMobile={isMobile}
                />
                {/* Tablet (isMobile === 3) hides the header row entirely, so the type
                    picker has to live in the toolbar there or it is unreachable. */}
                {isMobile === 3 && (
                    <div className="px-3 pb-3">
                        <EvaluationTypeSelector
                            types={types}
                            value={selectedType?.id}
                            onChange={handleTypeChange}
                            fullWidth
                        />
                    </div>
                )}
                <EvaluationsTable
                    scrollHeight={scrollHeight}
                    groupedData={groupedData}
                    monthColumns={monthColumns}
                    visibleTeams={visibleTeams}
                    expandedTeamId={expandedTeamId}
                    onToggleGroup={toggleGroupExpansion}
                    year={year}
                    onClickCell={showModalEditAdd}
                />
            </div>
            <AddEvaluationModal selectedDate={date} selectedTeamId={selectedTeamId} />
        </div>
    );
}
